import {
  VYBY_PRESET_KIND_CAMERA_BODY,
  VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  VYBY_PRESET_KIND_CAMERA_FOCAL,
  VYBY_PRESET_KIND_CAMERA_LENS,
  VYBY_PRESET_KIND_CAMERA_MOTION,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
  normalizeVybyPresetRow,
  normalizeVybyPresetRows,
} from '@/etc/vybyPresetCatalog'

export const CAMERA_BUILDER_KIND_CONFIG = Object.freeze([
  {
    kind: VYBY_PRESET_KIND_CAMERA_BODY,
    label: 'Cameras',
    singular: 'Camera',
    aspectRatio: '16:9',
    compatibilityField: 'compatibleLensIds',
  },
  {
    kind: VYBY_PRESET_KIND_CAMERA_LENS,
    label: 'Lens Systems',
    singular: 'Lens System',
    aspectRatio: '16:9',
    compatibilityField: 'compatibleFocalStyleIds',
  },
  {
    kind: VYBY_PRESET_KIND_CAMERA_FOCAL,
    label: 'Focal Styles',
    singular: 'Focal Style',
    aspectRatio: '16:9',
    compatibilityField: '',
  },
  {
    kind: VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
    label: 'Depth of Field',
    singular: 'Depth of Field',
    aspectRatio: '16:9',
    compatibilityField: '',
  },
  {
    kind: VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
    label: 'Transition In',
    singular: 'Transition In',
    aspectRatio: '1:1',
    compatibilityField: '',
  },
  {
    kind: VYBY_PRESET_KIND_CAMERA_MOTION,
    label: 'Motion',
    singular: 'Motion',
    aspectRatio: '1:1',
    compatibilityField: '',
  },
  {
    kind: VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
    label: 'Transition Out',
    singular: 'Transition Out',
    aspectRatio: '1:1',
    compatibilityField: '',
  },
])

export const CAMERA_BUILDER_KINDS = Object.freeze(
  CAMERA_BUILDER_KIND_CONFIG.map((entry) => entry.kind),
)

const configByKind = Object.freeze(
  Object.fromEntries(
    CAMERA_BUILDER_KIND_CONFIG.map((entry) => [entry.kind, entry]),
  ),
)

const normalizeIdList = (value) => {
  const list = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : []
  return [...new Set(list.map((item) => `${item || ''}`.trim()).filter(Boolean))]
}

export const cameraPresetKindConfig = (kind) =>
  configByKind[`${kind || ''}`] || null

export const cameraPresetKindAspectRatio = (kind) =>
  cameraPresetKindConfig(kind)?.aspectRatio || '16:9'

export const cameraPresetCompatibilityField = (kind) =>
  cameraPresetKindConfig(kind)?.compatibilityField || ''

export const cameraPresetRowToEntry = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  const config = cameraPresetKindConfig(normalized?.kind)
  if (!normalized || !config) return null

  const payload = normalized.payload || {}
  const compatibilityField = config.compatibilityField
  const compatibleIds = compatibilityField
    ? normalizeIdList(
        payload[compatibilityField] ||
          payload.compatibleIds ||
          payload.compatibleWith,
      )
    : []
  const promptPhrase = `${
    payload.promptPhrase ||
    payload.prompt ||
    payload.instruction ||
    normalized.name ||
    ''
  }`.trim()

  return {
    id: normalized.stableId,
    stableId: normalized.stableId,
    presetId: normalized.id || '',
    kind: normalized.kind,
    name: normalized.name,
    description: normalized.description || '',
    promptPhrase,
    thumbnail: normalized.image || '',
    category: normalized.category || '',
    compatibleIds,
    aspectRatio: config.aspectRatio,
    enabled: normalized.enabled !== false,
    sortOrder: normalized.sortOrder,
    source: 'global',
  }
}

export const createEmptyCameraPresetCatalog = () => {
  const byKind = Object.fromEntries(
    CAMERA_BUILDER_KINDS.map((kind) => [kind, []]),
  )
  return {
    byKind,
    byId: {},
    entries: [],
  }
}

export const buildCameraPresetCatalog = (rows = []) => {
  const catalog = createEmptyCameraPresetCatalog()
  const entries = normalizeVybyPresetRows(rows)
    .map(cameraPresetRowToEntry)
    .filter(Boolean)

  entries.forEach((entry) => {
    catalog.byKind[entry.kind].push(entry)
    catalog.byId[entry.stableId] = entry
  })
  catalog.entries = entries
  return catalog
}

export const cameraCatalogEntries = (catalog, kind) =>
  Array.isArray(catalog?.byKind?.[kind]) ? catalog.byKind[kind] : []

export const cameraCatalogEntry = (catalog, id, expectedKind = '') => {
  const normalizedId = `${id || ''}`
  const entry = expectedKind
    ? cameraCatalogEntries(catalog, expectedKind).find(
        (candidate) => candidate.stableId === normalizedId,
      ) || null
    : catalog?.byId?.[normalizedId] || null
  if (!entry) return null
  return entry
}

export const cameraSupportsLens = (catalog, cameraId, lensId) => {
  const camera = cameraCatalogEntry(
    catalog,
    cameraId,
    VYBY_PRESET_KIND_CAMERA_BODY,
  )
  return !!camera && camera.compatibleIds.includes(`${lensId || ''}`)
}

export const lensSupportsFocalStyle = (catalog, lensId, focalStyleId) => {
  const lens = cameraCatalogEntry(
    catalog,
    lensId,
    VYBY_PRESET_KIND_CAMERA_LENS,
  )
  return !!lens && lens.compatibleIds.includes(`${focalStyleId || ''}`)
}

export const validateCameraPresetCatalog = (catalog) => {
  const errors = []
  const warnings = []
  const seenPhrases = new Map()
  const seenIds = new Map()

  CAMERA_BUILDER_KIND_CONFIG.forEach(({ kind, label }) => {
    cameraCatalogEntries(catalog, kind).forEach((entry) => {
      if (!entry.promptPhrase) {
        errors.push(`${label}: "${entry.name}" is missing a prompt phrase.`)
      }
      if (!entry.thumbnail) {
        warnings.push(`${label}: "${entry.name}" is missing a thumbnail.`)
      }
      if (seenIds.has(entry.stableId)) {
        errors.push(
          `${label}: "${entry.name}" reuses stable ID "${entry.stableId}" from "${seenIds.get(entry.stableId)}".`,
        )
      } else {
        seenIds.set(entry.stableId, entry.name)
      }
      const phraseKey = `${kind}:${entry.promptPhrase.toLowerCase()}`
      if (seenPhrases.has(phraseKey)) {
        warnings.push(
          `${label}: "${entry.name}" duplicates the prompt phrase used by "${seenPhrases.get(phraseKey)}".`,
        )
      } else {
        seenPhrases.set(phraseKey, entry.name)
      }
    })
  })

  const validateCompatibility = (
    sourceKind,
    targetKind,
    emptyMessage,
    missingLabel,
  ) => {
    cameraCatalogEntries(catalog, sourceKind).forEach((entry) => {
      if (!entry.compatibleIds.length) {
        errors.push(`${emptyMessage}: "${entry.name}".`)
      }
      entry.compatibleIds.forEach((id) => {
        if (!cameraCatalogEntry(catalog, id, targetKind)) {
          errors.push(`${missingLabel}: "${entry.name}" references "${id}".`)
        }
      })
    })
  }

  validateCompatibility(
    VYBY_PRESET_KIND_CAMERA_BODY,
    VYBY_PRESET_KIND_CAMERA_LENS,
    'Camera has no compatible lens systems',
    'Missing compatible lens system',
  )
  validateCompatibility(
    VYBY_PRESET_KIND_CAMERA_LENS,
    VYBY_PRESET_KIND_CAMERA_FOCAL,
    'Lens system has no compatible focal styles',
    'Missing compatible focal style',
  )

  return {
    ok: errors.length === 0,
    errors,
    warnings,
  }
}
